/*
 * Declarative shadow DOM is only initialized on the initial render of the page.
 * If the component is mounted after the browser finishes the initial render,
 * the shadow root needs to be manually hydrated.
 */
export class DeclarativeShadowElement extends HTMLElement {
  connectedCallback() {
    if (!this.shadowRoot) {
      const template = this.querySelector(':scope > template[shadowrootmode="open"]');

      if (!(template instanceof HTMLTemplateElement)) return;

      const shadow = this.attachShadow({ mode: 'open' });
      shadow.append(template.content.cloneNode(true));
    }
  }
}

/**
 * A custom ResizeObserver that only calls the callback when the element is resized.
 * By default the ResizeObserver callback is called when the element is first observed.
 */
export class ResizeNotifier extends ResizeObserver {
  #initialized = false;

  /**
   * @param {ResizeObserverCallback} callback
   */
  constructor(callback) {
    super((entries) => {
      if (this.#initialized) return callback(entries, this);
      this.#initialized = true;
    });
  }

  disconnect() {
    this.#initialized = false;
    super.disconnect();
  }
}

/**
 * Event class for overflow minimum items updates
 * @extends {Event}
 */
export class OverflowMinimumEvent extends Event {
  /**
   * Creates a new OverflowMinimumEvent
   * @param {boolean} minimumReached - Whether the minimum number of visible items has been reached
   */
  constructor(minimumReached) {
    super('overflowMinimum', { bubbles: true });
    this.detail = {
      minimumReached,
    };
  }
}

export class ReflowEvent extends Event {
  /**
   * @param {HTMLElement} lastVisibleElement - The element to move to the last visible position
   */
  constructor(lastVisibleElement) {
    super('reflow', { bubbles: true });
    this.detail = { lastVisibleElement };
  }
}

const deferredPlpOverflowLists = new WeakSet();
let plpOverflowObserver;

function observePlpOverflow(element) {
  if (!plpOverflowObserver) {
    plpOverflowObserver = new IntersectionObserver((entries) => {
      // N-GRIDJS: every list that came near the viewport in this callback is set up in one
      // batch, so a row of cards (or a batch of deferred tiles) costs a few layouts in total
      // instead of three or four per list.
      const lists = [];
      for (const entry of entries) {
        if (entry.isIntersecting && deferredPlpOverflowLists.has(entry.target)) lists.push(entry.target);
      }
      if (lists.length) OverflowList.initializeDeferredPlpLists(lists);
    }, { rootMargin: '200px 0px' });
  }
  deferredPlpOverflowLists.add(element);
  plpOverflowObserver.observe(element);
}

function unobservePlpOverflow(element) {
  plpOverflowObserver?.unobserve(element);
  deferredPlpOverflowLists.delete(element);
}

/**
 * A custom element that wraps a list of items and moves them to an overflow slot when they don't fit.
 * This component is used in the header section, it needs to be render-blocking to avoid layout shifts.
 * @attr {string | null} minimum-items When set, the element enters a 'minimum-reached' state when visible items are at or below this number.
 * @example
 * <overflow-list minimum-items="2">
 *   <!-- list items -->
 * </overflow-list>
 */
export class OverflowList extends DeclarativeShadowElement {
  static get observedAttributes() {
    return ['disabled', 'minimum-items'];
  }

  /**
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'disabled') {
      if (newValue === 'true') {
        this.#reset();
      } else {
        this.#reflowItems();
      }
    }
  }

  connectedCallback() {
    super.connectedCallback();

    if (
      this.hasAttribute('data-plp-modal-preview') &&
      this.closest('main[data-template="collection"]') &&
      typeof IntersectionObserver === 'function'
    ) {
      this.#isDeferredPlpList = true;
      this.addEventListener('reflow', this.#handlePlpReflow);
      for (const event of ['focusin', 'pointerover', 'pointerdown']) {
        this.addEventListener(event, this.#handlePlpIntent);
      }
      observePlpOverflow(this);
      return;
    }

    if (this.hasAttribute('defer')) {
      // N-GRIDJS: deferred lists (card swatches outside the default collection grid: landing
      // collections, sliders, search) used to get one idle callback each, and each reflow
      // read and wrote layout in turn, so N lists cost about 3N forced layouts in one frame.
      // They now share one idle callback and one phased reflow (OverflowList.#reflowBatch).
      OverflowList.#queueDeferred(this);
    } else if (this.shadowRoot) {
      this.#initialize();
    } else {
      // Not all element children has been parsed yet, try again in the next tick
      // <overflow-list> is a special case as critical.js can execute before DOMContentLoaded
      setTimeout(() => {
        this.#initialize();
      }, 0);
    }
  }

  /**
   * Find the shadow parts and listen for reflow requests (no layout work)
   */
  #setup() {
    const { shadowRoot } = this;

    if (!shadowRoot) throw new Error('Missing shadow root');

    const defaultSlot = shadowRoot.querySelector('slot:not([name])');
    const overflowSlot = shadowRoot.querySelector('slot[name="overflow"]');
    const moreSlot = shadowRoot.querySelector('slot[name="more"]');
    const overflow = shadowRoot.querySelector('[part="overflow"]');
    const list = shadowRoot.querySelector('[part="list"]');
    const placeholder = shadowRoot.querySelector('[part="placeholder"]');

    if (
      !(defaultSlot instanceof HTMLSlotElement) ||
      !(overflowSlot instanceof HTMLSlotElement) ||
      !(moreSlot instanceof HTMLSlotElement) ||
      !(overflow instanceof HTMLElement) ||
      !(list instanceof HTMLUListElement) ||
      !(placeholder instanceof HTMLLIElement)
    ) {
      throw new Error('Invalid element types in <OverflowList />');
    }

    this.#refs = {
      defaultSlot,
      overflowSlot,
      moreSlot,
      overflow,
      list,
      placeholder,
    };

    // Add event listener for reflow requests
    if (!this.#isDeferredPlpList) {
      this.addEventListener(
        'reflow',
        /** @param {ReflowEvent} event */ (event) => {
          this.#reflowItems(event.detail.lastVisibleElement);
        }
      );
    }
  }

  /**
   * Initialize the element
   */
  #initialize(lastVisibleElement) {
    this.#setup();
    this.#reflowItems(lastVisibleElement);
    this.#plpInitialized = true;
  }

  static #pendingDeferred = new Set();
  static #deferredScheduled = false;

  /** @param {OverflowList} list */
  static #queueDeferred(list) {
    OverflowList.#pendingDeferred.add(list);
    if (OverflowList.#deferredScheduled) return;
    OverflowList.#deferredScheduled = true;
    const { schedule } = list;
    const requestIdleCallback =
      typeof window.requestIdleCallback === 'function' ? window.requestIdleCallback : setTimeout;
    requestIdleCallback(() => schedule(OverflowList.#flushDeferred));
  }

  static #flushDeferred = () => {
    OverflowList.#deferredScheduled = false;
    const lists = [...OverflowList.#pendingDeferred];
    OverflowList.#pendingDeferred.clear();
    /** @param {OverflowList} list */
    const prepare = (list) => {
      // Remove attribute first to change layout before calculating the actual size
      list.removeAttribute('defer');
      return true;
    };
    if (lists.some((list) => list.hasAttribute('data-reflow-steps'))) {
      OverflowList.#initializeInSteps(lists, prepare);
    } else {
      OverflowList.#initializeBatch(lists, prepare);
    }
  };

  /**
   * "Swatch rows set up in short steps" (theme setting, data-reflow-steps from
   * snippets/overflow-list.liquid): the deferred lists are set up a few at a time, each step its
   * own task, instead of all in one task that can block the page for tens of milliseconds. The
   * first list (the header menu, first in the page) gets a step of its own; each later step
   * takes as many lists as fit in about 10 ms, judged from the step before. Each step is the same
   * phased batch as before, so every list ends exactly as it did.
   * @param {OverflowList[]} lists
   * @param {(list: OverflowList) => boolean} prepare
   */
  static #initializeInSteps(lists, prepare) {
    const budgetMs = 10;
    let next = 0;
    let size = 1;
    const step = () => {
      const started = performance.now();
      const chunk = lists.slice(next, next + size);
      next += chunk.length;
      OverflowList.#initializeBatch(chunk, prepare);
      if (next >= lists.length) return;
      const perList = Math.max(performance.now() - started, 0.1) / chunk.length;
      size = Math.min(32, Math.max(1, Math.floor(budgetMs / perList)));
      setTimeout(step, 0);
    };
    step();
  }

  /**
   * Default collection grid: set up every listed card swatch list that is near the viewport,
   * in one batch. Lists that still wait for their markup or are already set up are left alone.
   * @param {Element[]} elements
   */
  static initializeDeferredPlpLists(elements) {
    const lists = elements.filter((element) => element instanceof OverflowList);
    OverflowList.#initializeBatch(lists, (list) => list.#preparePlp());
  }

  /**
   * #initialize for several lists at once, used by both deferred paths. `prepare` runs a list's
   * own first steps and returns false to leave it out; the others find their parts (#setup) and
   * share one phased reflow (#reflowBatch). A list that throws (missing shadow root, unexpected
   * parts) reports the same error as before, but does not stop the batch: the lists set up
   * before it would otherwise be left without their reflow and never marked initialised.
   * @param {OverflowList[]} lists
   * @param {(list: OverflowList) => boolean} prepare
   */
  static #initializeBatch(lists, prepare) {
    const ready = [];
    for (const list of lists) {
      try {
        if (!prepare(list)) continue;
        list.#setup();
        ready.push(list);
      } catch (error) {
        queueMicrotask(() => {
          throw error;
        });
      }
    }
    OverflowList.#reflowBatch(ready.map((list) => [list, undefined]));
    for (const list of ready) list.#plpInitialized = true;
  }

  disconnectedCallback() {
    this.#resizeObserver.disconnect();
    if (this.#isDeferredPlpList) {
      this.#stopPlpIntentObservation();
      this.#mutationObserver.disconnect();
      this.removeEventListener('reflow', this.#handlePlpReflow);
      this.#plpInitialized = false;
      this.#isDeferredPlpList = false;
    }
  }

  #isDeferredPlpList = false;
  #plpInitialized = false;

  #stopPlpIntentObservation() {
    unobservePlpOverflow(this);
    for (const event of ['focusin', 'pointerover', 'pointerdown']) {
      this.removeEventListener(event, this.#handlePlpIntent);
    }
    document.removeEventListener('DOMContentLoaded', this.#handlePlpIntent);
  }

  /**
   * The steps before a default collection grid list is set up, shared by #initializePlp and
   * the near-viewport batch. Returns false when the list is gone, already set up, or waits
   * for DOMContentLoaded for its markup.
   * While the page is still loading, the parser may have attached the declarative shadow root
   * but not yet written all of its parts (the list arrives in pieces). A list set up then fails
   * in #setup ("Invalid element types") and is never set up again, so its swatches wrap instead
   * of ending in "+N". Such a list also waits for DOMContentLoaded.
   * @returns {boolean}
   */
  #preparePlp() {
    if (!this.isConnected || this.#plpInitialized) return false;
    super.connectedCallback();
    if (document.readyState === 'loading' && !this.#hasShadowParts()) {
      document.addEventListener('DOMContentLoaded', this.#handlePlpIntent, { once: true });
      return false;
    }
    this.#stopPlpIntentObservation();
    this.removeAttribute('defer');
    return true;
  }

  /**
   * Whether the shadow root holds every part #setup reads (the last one parsed is the overflow
   * slot inside [part="overflow"], after the list).
   * @returns {boolean}
   */
  #hasShadowParts() {
    const root = this.shadowRoot;
    if (!root) return false;
    return Boolean(
      root.querySelector('[part="list"] > slot:not([name])') &&
        root.querySelector('slot[name="more"]') &&
        root.querySelector('[part="placeholder"]') &&
        root.querySelector('[part="overflow"] slot[name="overflow"]')
    );
  }

  #initializePlp(lastVisibleElement) {
    if (this.#preparePlp()) this.#initialize(lastVisibleElement);
  }

  #handlePlpIntent = () => this.#initializePlp();

  #handlePlpReflow = (event) => {
    if (!this.#plpInitialized) {
      this.#initializePlp(event.detail?.lastVisibleElement);
      return;
    }
    this.#reflowItems(event.detail?.lastVisibleElement);
  };

  get schedule() {
    return typeof Theme?.utilities?.scheduler?.schedule === 'function'
      ? Theme.utilities.scheduler.schedule
      : /** @param {FrameRequestCallback} callback */ (callback) =>
          requestAnimationFrame(() => setTimeout(callback, 0));
  }

  #scheduled = false;

  /**
   * Get the minimum number of items before changing the minimum-reached state
   * @returns {number | null}
   */
  get minimumItems() {
    const value = this.getAttribute('minimum-items');
    return value ? parseInt(value, 10) : null;
  }

  get overflowSlot() {
    const { overflowSlot } = this.#refs;
    return overflowSlot;
  }

  get defaultSlot() {
    const { defaultSlot } = this.#refs;
    return defaultSlot;
  }

  /**
   * @type {{width: number | null, height: number | null}}
   */
  #lastDimensions = {
    width: null,
    height: null,
  };

  /**
   * @type {ResizeObserverCallback & MutationCallback}
   */
  #handleChange = (event) => {
    if (this.#scheduled) return;

    let width = null;
    let height = null;
    let isResize = false;

    for (const [, entry] of event.entries()) {
      if (!(entry instanceof ResizeObserverEntry)) break;
      // There should only be one entry
      isResize = true;
      width = Math.round(entry.contentRect.width);
      height = Math.round(entry.contentRect.height);
    }

    if (isResize) {
      if (!width || !height || (width === this.#lastDimensions.width && height === this.#lastDimensions.height)) {
        // Skip reflow if dimensions are 0 or the same as the last reflow
        return;
      }

      this.#lastDimensions = { width: Math.round(width), height: Math.round(height) };
    }

    this.#scheduled = true;

    this.schedule(() => {
      this.#reflowItems();
      this.#scheduled = false;
    });
  };

  /**
   * Move all items to the default slot.
   */
  #moveItemsToDefaultSlot() {
    const { defaultSlot, overflowSlot } = this.#refs;

    for (const element of overflowSlot.assignedElements()) {
      element.slot = defaultSlot.name;
    }
  }

  /**
   * Reset the list to its initial state and disconnect the observers.
   */
  #reset() {
    const { list } = this.#refs;

    this.#mutationObserver.disconnect();
    this.#resizeObserver.disconnect();
    this.#moveItemsToDefaultSlot();

    list.style.removeProperty('height');
    this.style.setProperty('--overflow-count', '0');
  }

  /**
   * Sets the minimum-reached attribute and dispatches a custom event based on visible elements count
   * @param {Element[]} visibleElements - The currently visible elements
   */
  #updateMinimumReached(visibleElements) {
    if (this.minimumItems !== null) {
      const minimumReached = visibleElements.length < this.minimumItems;

      if (minimumReached) {
        this.setAttribute('minimum-reached', '');
      } else {
        this.removeAttribute('minimum-reached');
      }

      this.dispatchEvent(new OverflowMinimumEvent(minimumReached));
    }
  }

  /**
   * Show all items in the list.
   */
  showAll() {
    if (this.#isDeferredPlpList && !this.#plpInitialized) this.#initializePlp();
    const { placeholder } = this.#refs;

    placeholder.style.setProperty('width', '0');
    placeholder.style.setProperty('display', 'none');
    this.setAttribute('disabled', 'true');
  }

  /**
   * Reflow items based on available space within the list.
   * @param {HTMLElement} [lastVisibleElement] Optional element to place in last visible position
   */
  #reflowItems = (lastVisibleElement) => {
    OverflowList.#reflowBatch([[this, lastVisibleElement]]);
  };

  /**
   * Reflow one or more lists. N-GRIDJS: the steps are the ones a single list always ran, in
   * the same order, but grouped into phases across the lists, so every read of a phase shares
   * one layout instead of each list forcing its own (about four forced layouts in total
   * instead of about four per list). With one list this is the old sequence, except that the
   * list's top is read again in the second measuring phase (same value, see there). The
   * lists' widths do not depend on each other, and positions are only compared within a list.
   * @param {Array<[OverflowList, HTMLElement | undefined]>} pairs
   */
  static #reflowBatch(pairs) {
    if (!pairs.length) return;
    const plans = pairs.map(([list, lastVisibleElement]) => ({ list, lastVisibleElement, refs: list.#refs }));

    // Write: stop observing while the list is rearranged.
    for (const { list } of plans) {
      list.#resizeObserver.disconnect();
      list.#mutationObserver.disconnect();
    }

    // Read: avoid layout shifts while reflowing the list.
    for (const plan of plans) {
      plan.height = plan.list.firstElementChild?.getBoundingClientRect().height;
    }

    // Write: fix the list height, move every item to the default slot, hide "More".
    for (const plan of plans) {
      const { list, refs, height } = plan;
      const { defaultSlot, moreSlot, list: listPart, placeholder } = refs;
      if (height) listPart.style.height = `${height}px`;
      listPart.style.setProperty('overflow', 'hidden');

      // Move all elements to the default slot so we can check which ones overflow
      list.#moveItemsToDefaultSlot();

      // A <template shadowrootmode> stays in the light DOM when the shadow root is attached from
      // script (markup injected with innerHTML), and it never renders a box, so it would always be
      // measured as overflowing and inflate the counter.
      plan.elements = defaultSlot
        .assignedElements()
        .filter((element) => !(element instanceof HTMLTemplateElement));
      plan.totalItems = Number.parseInt(list.dataset.totalItems || '', 10) || plan.elements.length;
      plan.hasUnrenderedItems = plan.totalItems > plan.elements.length;

      // Make sure the "More" item and placeholder are hidden
      moreSlot.hidden = true;
      placeholder.hidden = true;
    }

    // Read: which items fit on the first row.
    for (const plan of plans) {
      const rootRect = plan.refs.list.getBoundingClientRect();
      plan.rootTop = rootRect.top;

      // Store the initial dimensions for comparison later
      plan.list.#lastDimensions = { width: Math.round(rootRect.width), height: Math.round(rootRect.height) };

      plan.visibleElements = plan.elements.filter((el) => el.getBoundingClientRect().top <= plan.rootTop);
      plan.measureWithMore =
        plan.visibleElements.length !== plan.elements.length || !!plan.lastVisibleElement || plan.hasUnrenderedItems;
    }

    // Write: put the "More" item (and lastVisibleElement, if provided) at the start of the list
    // to see which items still fit on the same row.
    const withMore = plans.filter((plan) => plan.measureWithMore);
    for (const { refs, height, lastVisibleElement } of withMore) {
      refs.moreSlot.style.setProperty('order', '-1');
      refs.moreSlot.hidden = false;
      refs.moreSlot.style.setProperty('height', `${height}px`);
      if (lastVisibleElement) lastVisibleElement.style.setProperty('order', '-1');
    }

    // Read: recalculate the visible elements. The list's top is read again in the same layout:
    // another list in the batch may have moved this one, never its own items relative to it.
    for (const plan of withMore) {
      const rootTop = plan.refs.list.getBoundingClientRect().top;
      plan.visibleElements = plan.elements.filter((el) => el.getBoundingClientRect().top <= rootTop);
    }

    // Write: reset the order; work out what overflows (no layout reads).
    for (const plan of plans) {
      const { list, refs, lastVisibleElement } = plan;
      if (plan.measureWithMore) {
        refs.moreSlot.style.removeProperty('order');
        if (lastVisibleElement) lastVisibleElement.style.removeProperty('order');

        // If we have a lastVisibleElement, ensure it's in the last visible position
        if (lastVisibleElement) {
          const visibleIndex = plan.visibleElements.indexOf(lastVisibleElement);
          if (visibleIndex !== -1) {
            // Remove lastVisibleElement from its current position
            plan.visibleElements.splice(visibleIndex, 1);
            // Add it to the end of visible elements
            plan.visibleElements.push(lastVisibleElement);
          }
        }

        refs.moreSlot.style.setProperty('height', 'auto');
      }

      plan.overflowingElements = plan.elements.filter((element) => !plan.visibleElements.includes(element));

      // Collection cards only render a bounded set of swatches. Keep the final rendered
      // swatch available as the modal preview when the full set would otherwise fit.
      if (
        list.hasAttribute('data-plp-modal-preview') &&
        plan.hasUnrenderedItems &&
        plan.overflowingElements.length === 0
      ) {
        const previewElement = plan.visibleElements.pop();

        if (previewElement) plan.overflowingElements.push(previewElement);
      }

      [plan.firstOverflowingElement] = plan.overflowingElements;
      plan.hasOverflow = plan.overflowingElements.length > 0 || plan.totalItems > plan.visibleElements.length;
      plan.hiddenCount = Math.max(0, plan.totalItems - plan.visibleElements.length);
    }

    // Read: the placeholder takes the width of the first overflowing item.
    for (const plan of plans) {
      plan.placeholderWidth = plan.firstOverflowingElement ? plan.firstOverflowingElement.clientWidth : 0;
    }

    // Write: move the items to their slots and show the counter.
    for (const plan of plans) {
      const { list, refs } = plan;
      const { defaultSlot, overflowSlot, moreSlot, list: listPart, placeholder } = refs;
      const { overflowingElements, firstOverflowingElement, hasOverflow, hiddenCount } = plan;

      // Move the elements to the correct slot
      for (const element of plan.elements) {
        const slot = overflowingElements.includes(element) ? overflowSlot.name : defaultSlot.name;
        if (!list.#isDeferredPlpList || element.slot !== slot) element.slot = slot;
      }

      // If there are overflowing elements
      // Show more button and placeholder if needed
      moreSlot.hidden = !hasOverflow;

      if (hasOverflow) {
        // Set the width and height of the placeholder so the list can grow if there is space
        placeholder.style.width = `${plan.placeholderWidth}px`;
        placeholder.hidden = false;
      }

      if (list.hasAttribute('data-plp-modal-preview') && firstOverflowingElement) {
        const moreContainer = moreSlot.assignedElements()[0];
        const moreButton = moreContainer?.querySelector('.hidden-swatches__count--modal-preview');
        const previewSwatch = firstOverflowingElement.querySelector('.swatch');
        const previewInput = firstOverflowingElement.querySelector('input[data-variant-id]');

        if (moreButton instanceof HTMLButtonElement && previewSwatch instanceof HTMLElement) {
          if (
            !list.#isDeferredPlpList ||
            moreButton.childNodes.length !== 1 ||
            !moreButton.firstChild?.isEqualNode(previewSwatch)
          ) {
            moreButton.replaceChildren(previewSwatch.cloneNode(true));
          }
          const baseProductUrl = list.dataset.productUrl || moreButton.dataset.productUrl || '';
          const variantId = previewInput instanceof HTMLInputElement ? previewInput.dataset.variantId : '';
          moreButton.dataset.productUrl = variantId
            ? `${baseProductUrl}${baseProductUrl.includes('?') ? '&' : '?'}variant=${variantId}`
            : baseProductUrl;
        }
      }

      listPart.style.setProperty('counter-reset', `overflow-count ${hiddenCount}`);
      list.style.setProperty('--overflow-count', `${hiddenCount}`);
      listPart.style.removeProperty('overflow');

      // Check if the minimum-reached state should be updated
      hasOverflow && list.#updateMinimumReached(plan.visibleElements);

      // Observe the list for changes in size
      list.#resizeObserver.observe(list);
      list.#mutationObserver.observe(list, { childList: true });
    }
  }

  /**
   * @type {{
   *   defaultSlot: HTMLSlotElement;
   *   overflowSlot: HTMLSlotElement;
   *   moreSlot: HTMLSlotElement;
   *   overflow: HTMLElement;
   *   list: HTMLUListElement;
   *   placeholder: HTMLLIElement;
   * }}
   */
  #refs;

  /**
   * @type {ResizeObserver}
   */
  #resizeObserver = new ResizeNotifier(this.#handleChange);

  /**
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver(this.#handleChange);
}

if (!customElements.get('overflow-list')) {
  customElements.define('overflow-list', OverflowList);
}

// Function to calculate total height of header group children
export function calculateHeaderGroupHeight(
  header = document.querySelector('#header-component'),
  headerGroup = document.querySelector('#header-group')
) {
  if (!headerGroup) return 0;

  let totalHeight = 0;
  const children = headerGroup.children;
  for (let i = 0; i < children.length; i++) {
    const element = children[i];
    if (element === header || !(element instanceof HTMLElement)) continue;
    totalHeight += element.offsetHeight;
  }

  // If the header is transparent and has a sibling section, add the height of the header to the total height
  if (header instanceof HTMLElement && header.hasAttribute('transparent') && header.parentElement?.nextElementSibling) {
    return totalHeight + header.offsetHeight;
  }

  return totalHeight;
}

/**
 * Initialize and maintain header height CSS variables.
 * This is critical for preventing layout shifts during page load.
 * There is a `ResizeObserver` and `MutationObserver` that kicks in post hydration in header.js
 * Note: header-group uses display: contents, so we must observe all children.
 */
function updateHeaderHeights() {
  const header = document.querySelector('header-component');

  // Early exit if no header, or if <body> has not been parsed yet - nothing to write to.
  if (!(header instanceof HTMLElement) || !document.body) return;

  // Calculate initial height(s
  const headerHeight = header.offsetHeight;
  const headerGroupHeight = calculateHeaderGroupHeight(header);

  document.body.style.setProperty('--header-height', `${headerHeight}px`);
  document.body.style.setProperty('--header-group-height', `${headerGroupHeight}px`);
}

/**
 * Updates CSS custom properties for transparent header offset calculation
 * Avoids expensive :has() selectors
 */
function updateTransparentHeaderOffset() {
  if (!document.body) return;

  const header = document.querySelector('#header-component');
  const headerGroup = document.querySelector('#header-group');
  const hasHeaderSection = headerGroup?.querySelector('.header-section');
  if (!hasHeaderSection || !header?.hasAttribute('transparent')) {
    document.body.style.setProperty('--transparent-header-offset-boolean', '0');
    return;
  }

  const hasImmediateSection = hasHeaderSection.nextElementSibling?.classList.contains('shopify-section');

  const shouldApplyOffset = !hasImmediateSection ? '1' : '0';
  document.body.style.setProperty('--transparent-header-offset-boolean', shouldApplyOffset);
}

export function updateAllHeaderCustomProperties() {
  updateHeaderHeights();
  updateTransparentHeaderOffset();
}

/**
 * TWS-1345: this module is also pulled into <head> as a dynamic module (snippets/scripts.liquid
 * `lazyModule`, and every module that imports @theme/critical), so evaluation can win the race
 * against the parser reaching the header. Measuring then is not just unsafe but wrong:
 * `document.body` may still be null, and a header that is not in the DOM yet reads as "no
 * transparent header", which would pin the offset to 0 for the life of the page.
 *
 * Nothing re-runs this afterwards - header.js only keeps `--header-height` in sync - so the run
 * has to wait for the DOM in that case. The eager tag at the end of layout/theme.liquid sits
 * after #header-group, so it still measures synchronously and the variables are in place on the
 * first frame, which is what keeps the header from shifting layout.
 */
function initHeaderCustomProperties() {
  const headerParsed = Boolean(document.body && document.querySelector('#header-group'));

  if (!headerParsed && document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', updateAllHeaderCustomProperties, { once: true });
    return;
  }

  updateAllHeaderCustomProperties();
}

initHeaderCustomProperties();
